import React from 'react';
import {
    Box,
    VStack,
    HStack,
    Input,
    InputGroup,
    InputLeftElement,
    Icon,
    Text,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    Grid,
    GridItem,
    Button,
    IconButton,
    Tag,
    Center,
    Badge,
} from '@chakra-ui/react';
import { AiOutlineSearch, AiOutlineClose } from 'react-icons/ai';
import { ReportingShellViewBaseConfig } from '../../../base';
import {
    InsertOverviewContainerProps,
    InsertOverviewViewProps,
} from './InsertOverviewProps';
import { InsertTemplateItemProps } from './template';
import { InsertComponentItemProps } from './component';

export function createInsertOverviewView(
    config: ReportingShellViewBaseConfig & {}
): React.FC<InsertOverviewViewProps> {
    return (props) => {
        // Get search value for filtering
        const searchValue = String(props.search.input.value || '');

        // Get icon based on item type

        const TemplateItem: React.FC<InsertTemplateItemProps> = ({
            id,
            label,
            description,
            caption,
            author,
            icon,
        }) => (
            <HStack
                w="full"
                h={16}
                align="center"
                borderRadius="md"
                cursor="pointer"
                overflow="hidden"
                borderWidth={2}
                borderColor="whiteAlpha.200"
                role="group"
                userSelect="none"
                spacing={0}
                _hover={{ bg: 'whiteAlpha.50' }}
                _focus={{
                    outline: 'none',
                    bg: 'whiteAlpha.50',
                    borderColor: 'whiteAlpha.300',
                }}
            >
                <Center h={16} w={16} bg="whiteAlpha.100" flexShrink={0}>
                    {icon.kind === 'emoji' ? (
                        <Text fontSize="xl">{icon.value}</Text>
                    ) : (
                        <Icon as={icon.Icon} fontSize="xl" />
                    )}
                </Center>
                <HStack w="full" justify="space-between" px={4} py={4}>
                    <VStack align="start" spacing={0} w="full">
                        <HStack w="full" justify="space-between" align="start">
                            <Text
                                color="whiteAlpha.800"
                                fontWeight="semibold"
                                noOfLines={1}
                                fontSize="sm"
                                flex={1}
                                pr={2}
                            >
                                {label}
                            </Text>
                            <Badge
                                colorScheme="blue"
                                variant="subtle"
                                size="xs"
                                flexShrink={0}
                            >
                                {author}
                            </Badge>
                        </HStack>
                        <Text color="whiteAlpha.600" noOfLines={1} fontSize="sm">
                            {caption}
                        </Text>
                    </VStack>
                </HStack>
            </HStack>
        );

        const ComponentItem: React.FC<InsertComponentItemProps> = ({
            id,
            label,
            description,
            caption,
            icon,
        }) => (
            <HStack
                w="full"
                h={16}
                align="center"
                borderRadius="md"
                cursor="pointer"
                overflow="hidden"
                borderWidth={2}
                borderColor="whiteAlpha.200"
                role="group"
                userSelect="none"
                spacing={0}
                _hover={{ bg: 'whiteAlpha.50' }}
                _focus={{
                    outline: 'none',
                    bg: 'whiteAlpha.50',
                    borderColor: 'whiteAlpha.300',
                }}
            >
                <Center h={16} w={16} bg="whiteAlpha.100" flexShrink={0}>
                    {icon.kind === 'emoji' ? (
                        <Text fontSize="xl">{icon.value}</Text>
                    ) : (
                        <Icon as={icon.Icon} fontSize="xl" />
                    )}
                </Center>
                <HStack w="full" justify="space-between" px={4} py={4}>
                    <VStack align="start" spacing={0} w="full">
                        <Text
                            color="whiteAlpha.800"
                            fontWeight="semibold"
                            noOfLines={1}
                            fontSize="sm"
                        >
                            {label}
                        </Text>
                        <Text color="whiteAlpha.600" noOfLines={1} fontSize="sm">
                            {caption}
                        </Text>
                    </VStack>
                </HStack>
            </HStack>
        );

        return (
            <Box
                w="full"
                h="full"
                display="grid"
                gridTemplateRows="auto auto 1fr"
                gridTemplateColumns="1fr"
                overflow="hidden"
            >
                {/* Search - Fixed height at top */}
                <Box
                    gridRow="1"
                    w="full"
                    p={4}
                    // borderBottomWidth={1}
                    // borderColor="whiteAlpha.200"
                >
                    <InputGroup fontSize="sm">
                        <InputLeftElement>
                            <Icon
                                as={AiOutlineSearch}
                                color="whiteAlpha.500"
                                fontSize="lg"
                            />
                        </InputLeftElement>
                        <Input
                            {...props.search.input}
                            borderColor="whiteAlpha.100"
                            bg="whiteAlpha.100"
                            _hover={{
                                borderColor: 'whiteAlpha.200',
                            }}
                            _focus={{
                                outline: 'none',
                                borderColor: 'blue.300',
                            }}
                            _focusVisible={{
                                outline: 'none',
                                borderColor: 'blue.300',
                            }}
                        />
                        {props.search.input.value && (
                            <Box
                                position="absolute"
                                right={2}
                                top="50%"
                                transform="translateY(-50%)"
                            >
                                <IconButton
                                    aria-label="Clear search"
                                    icon={<Icon as={AiOutlineClose} />}
                                    size="xs"
                                    variant="ghost"
                                    color="whiteAlpha.500"
                                    onClick={() =>
                                        props.search.input.onChange?.({
                                            target: { value: '' },
                                        } as any)
                                    }
                                    _hover={{ color: 'whiteAlpha.700' }}
                                    _focus={{ outline: 'none' }}
                                />
                            </Box>
                        )}
                    </InputGroup>
                </Box>

                {/* Tabs - Fixed height and content area */}
                <Box gridRow="2 / -1" h="full">
                    <Tabs
                        {...props.tabs.root}
                        h="full"
                        isFitted={true}
                        variant="line"
                        size="sm"
                        display="flex"
                        flexDirection="column"
                    >
                        <TabList fontWeight="semibold">
                            {props.tabs.items.map((item) => (
                                <Tab
                                    key={item.id}
                                    py={2}
                                    fontWeight="semibold"
                                    color="whiteAlpha.600"
                                    _selected={{
                                        color: 'whiteAlpha.800',
                                        bg: 'whiteAlpha.100',
                                        borderColor: 'blue.300',
                                    }}
                                    _hover={{
                                        color: 'whiteAlpha.800',
                                        bg: 'whiteAlpha.50',
                                    }}
                                    _focus={{ outline: 'none' }}
                                    _focusVisible={{ outline: 'none' }}
                                >
                                    {item.label}
                                </Tab>
                            ))}
                        </TabList>
                        <TabPanels flex={1} h="full" position="relative">
                            {props.tabs.items.map((tabItem) => (
                                <TabPanel
                                    key={tabItem.id}
                                    p={0}
                                    position="absolute"
                                    top={0}
                                    left={0}
                                    right={0}
                                    bottom={0}
                                    display="grid"
                                    gridTemplateRows="1fr auto"
                                >
                                    <Box
                                        gridRow="1"
                                        overflowY="auto"
                                        sx={{
                                            scrollbarGutter: 'stable',
                                            '&::-webkit-scrollbar': {
                                                '-webkit-appearance': 'none',
                                                width: '0.5rem',
                                            },
                                            '&::-webkit-scrollbar-track': {
                                                background: 'transparent',
                                            },
                                            '&::-webkit-scrollbar-corner': {
                                                '-webkit-appearance': 'none',
                                            },
                                            '&::-webkit-scrollbar-thumb': {
                                                borderRadius: '1rem',
                                                background: 'rgba(255,255,255,0.3)',
                                            },
                                        }}
                                    >
                                        <Box px={4} pt={4} pb={6}>
                                            <Grid templateColumns="1fr" gap={3}>
                                                {tabItem.items
                                                    .filter(
                                                        (item) =>
                                                            item.label
                                                                .toLowerCase()
                                                                .includes(
                                                                    searchValue.toLowerCase()
                                                                ) ||
                                                            item.description
                                                                .toLowerCase()
                                                                .includes(
                                                                    searchValue.toLowerCase()
                                                                )
                                                    )
                                                    .map((item) => (
                                                        <GridItem key={item.id}>
                                                            {tabItem.kind ===
                                                            'template' ? (
                                                                <TemplateItem
                                                                    {...(item as InsertTemplateItemProps)}
                                                                />
                                                            ) : (
                                                                <ComponentItem
                                                                    {...(item as InsertComponentItemProps)}
                                                                />
                                                            )}
                                                        </GridItem>
                                                    ))}
                                            </Grid>
                                        </Box>
                                    </Box>

                                    {/* Footer with explore more button */}
                                    <HStack
                                        gridRow="2"
                                        justify="center"
                                        w="full"
                                        p={4}
                                        bg="whiteAlpha.100"
                                        borderTopWidth={2}
                                        borderColor="whiteAlpha.200"
                                    >
                                        <Button
                                            {...tabItem.footer.button}
                                            variant="link"
                                            color="blue.300"
                                            fontSize="sm"
                                            _hover={{ color: 'blue.200' }}
                                            _focus={{ outline: 'none' }}
                                        >
                                            {tabItem.footer.label}
                                        </Button>
                                    </HStack>
                                </TabPanel>
                            ))}
                        </TabPanels>
                    </Tabs>
                </Box>
            </Box>
        );
    };
}
